import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { AccountBalance, LedgerAccount } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { amountValue, parseAmount } from "../../shared/lib/format";
import { DateField } from "../../shared/ui/DateField";
import { declareAccountState } from "./api";

interface AccountStatePanelProps {
  bookID: string;
  account: LedgerAccount;
  balance?: AccountBalance;
  onDone: (message: string) => void;
}

// AccountState records endpoint availability only. Facility limits and
// reported debt are Borrowing observations and are intentionally absent here.
export function AccountStatePanel({ bookID, account, balance, onDone }: AccountStatePanelProps) {
  const queryClient = useQueryClient();
  const state = balance?.state;
  const shown = (value?: { minor: number; currency: typeof account.currency }) =>
    (value ? amountValue(value.minor, value.currency) : "");
  const [form, setForm] = useState({
    availableFunds: shown(state?.availableFunds),
    observedAt: new Date().toISOString().slice(0, 10),
    source: "",
  });
  const [refused, setRefused] = useState("");
  // A nil field says nothing about itself, so retracting one needs its own
  // word: a bank that withdraws a facility has told you something, and
  // silence would leave the old limit standing forever.
  const [retract, setRetract] = useState<string[]>([]);
  const retracting = (field: string) => retract.includes(field);
  const toggleRetract = (field: string) => setRetract((fields) => (
    fields.includes(field) ? fields.filter((name) => name !== field) : [...fields, field]
  ));

  const declare = useMutation({
    mutationFn: () => {
      const body: Parameters<typeof declareAccountState>[2] = {
        observedAt: new Date(`${form.observedAt}T12:00:00Z`).toISOString(),
        source: form.source.trim() || undefined,
      };
      const available = parseAmount(form.availableFunds, true);
      if (available !== null && !retracting("availableFunds")) body.availableFunds = { amount: available, currency: account.currency };
      if (retract.length > 0) body.clear = retract;
      return declareAccountState(bookID, account.id, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      onDone("Recorded what the bank reports. No posting was written and no total moved.");
    },
  });

  return (
    <form
      id={`account-state-${account.id}`} className="stacked-form"
      onSubmit={(event) => {
        event.preventDefault();
        const filled = ([
          ["Available to spend", form.availableFunds, true, "availableFunds"],
        ] as const).filter(([, , , field]) => !retracting(field));
        if (filled.every(([, value]) => value.trim() === "") && retract.length === 0) {
          setRefused("Fill in at least one, or mark one as no longer known. An empty observation says nothing.");
          return;
        }
        const bad = filled.find(([, value, signed]) => value.trim() !== "" && parseAmount(value, signed) === null);
        if (bad) {
          setRefused(`${bad[0]} is not an amount. Digits, and at most three decimals.`);
          return;
        }
        setRefused("");
        declare.mutate();
      }}
    >
      <p className="panel-note">
        "What your institution reports about this endpoint. Facility limits and reported debt are recorded on the linked Borrowing, not on the Account."
      </p>
      <div className="field-grid">
        <label>
          Available to spend <small>everything you can spend now, unused credit included</small>
          <span className="amount-input">
            <input value={retracting("availableFunds") ? "" : form.availableFunds} disabled={retracting("availableFunds")}
              onChange={(event) => setForm({ ...form, availableFunds: event.target.value })}
              inputMode="decimal" placeholder="13000.00" autoFocus />
            <span>{account.currency}</span>
          </span>
          <label className="check retract">
            <input type="checkbox" checked={retracting("availableFunds")} onChange={() => toggleRetract("availableFunds")} />
            <span>No longer known</span>
          </label>
        </label>
      </div>
      <div className="field-grid">
        <div className="field">
          <span className="field-label">Reported on</span>
          <DateField label="Reported on" value={form.observedAt} onChange={(value) => setForm({ ...form, observedAt: value })} />
        </div>
        <label>Where it came from <small>optional</small>
          <input value={form.source} onChange={(event) => setForm({ ...form, source: event.target.value })} placeholder="September statement" />
        </label>
      </div>
      <p className="panel-note">Leave this empty unless the provider explicitly defines the value as endpoint availability.</p>
      {refused ? <p className="form-error" role="alert">{refused}</p> : null}
      {declare.error ? <p className="form-error" role="alert">{humanError(declare.error)}</p> : null}
    </form>
  );
}
